<template>
  <v-app>
    <!-- ── Sidebar ───────────────────────────────────────────────────── -->
    <v-navigation-drawer
      app
      permanent
      color="#1A1D27"
      dark
      width="220"
    >
      <!-- Logo -->
      <div class="sidebar-logo">
        <img v-bind:src="appOwnerLogo ? appOwnerLogo : '/favicon.png'" alt="logo" class="sidebar-logo__img" />
        <div>
          <div class="sidebar-logo__name">EVPN Manager</div>
          <div class="sidebar-logo__version"><span style="color: #fff">{{ appOwner }}</span> - v{{ appVersion }}</div>
        </div>
      </div>

      <v-list v-if="session_loggedIn" nav dense class="px-2">
        <v-subheader class="sidebar-section">Management</v-subheader>

        <v-list-item to="/" exact link class="nav-item">
          <v-list-item-icon><v-icon small>mdi-monitor-multiple</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title>Devices</v-list-item-title></v-list-item-content>
        </v-list-item>

        <v-list-item
          v-if="session_isAdmin"
          to="/groups-users"
          link
          class="nav-item"
        >
          <v-list-item-icon><v-icon small>mdi-account-group</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title>Groups &amp; Users</v-list-item-title></v-list-item-content>
        </v-list-item>

        <v-list-item to="/events" link class="nav-item">
          <v-list-item-icon><v-icon small>mdi-history</v-icon></v-list-item-icon>
          <v-list-item-content><v-list-item-title>Events</v-list-item-title></v-list-item-content>
        </v-list-item>

        <template v-if="session_isAdmin">
          <v-subheader class="sidebar-section">Config</v-subheader>

          <v-list-item link class="nav-item" v-on:click="showWireGuardTemplate = 'client'">
            <v-list-item-icon><v-icon small>mdi-shield-key-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>WG Client Template</v-list-item-title></v-list-item-content>
          </v-list-item>

          <v-list-item link class="nav-item" v-on:click="showWireGuardTemplate = 'server'">
            <v-list-item-icon><v-icon small>mdi-shield-lock-outline</v-icon></v-list-item-icon>
            <v-list-item-content><v-list-item-title>WG Server Config</v-list-item-title></v-list-item-content>
          </v-list-item>
        </template>
      </v-list>

      <template v-slot:append>
        <template v-if="session_loggedUser">
        <v-divider style="border-color:rgba(255,255,255,.08)" />
        <div class="sidebar-footer">
          <v-icon small class="mr-2" style="color:rgba(255,255,255,.4)">mdi-account-circle-outline</v-icon>
          <div class="sidebar-footer__info">
            <div class="sidebar-footer__name">{{ session_loggedUser.username }}</div>
          </div>
          <v-spacer />
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon x-small v-on="on" v-on:click="showChangePassword = true">
                <v-icon x-small style="color:rgba(255,255,255,.4)">mdi-lock-reset</v-icon>
              </v-btn>
            </template>
            <span>Change password</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn icon x-small v-on="on" v-on:click="logout">
                <v-icon x-small style="color:rgba(255,255,255,.4)">mdi-logout</v-icon>
              </v-btn>
            </template>
            <span>Logout</span>
          </v-tooltip>
        </div>
        </template>
      </template>
    </v-navigation-drawer>

    <!-- ── Topbar ────────────────────────────────────────────────────── -->
    <v-app-bar
      app
      flat
      height="56"
      v-bind:color="$vuetify.theme.dark ? '#161B22' : 'white'"
      class="topbar"
    >
      <span class="topbar__title">{{ $route.meta.title || appTitle }}</span>
      <v-spacer />
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn icon small v-on="on" v-on:click="toggleTheme">
            <v-icon small>{{ $vuetify.theme.dark ? 'mdi-white-balance-sunny' : 'mdi-weather-night' }}</v-icon>
          </v-btn>
        </template>
        <span>{{ $vuetify.theme.dark ? 'Light mode' : 'Dark mode' }}</span>
      </v-tooltip>
    </v-app-bar>

    <!-- ── Content ───────────────────────────────────────────────────── -->
    <v-main>
      <router-view />
    </v-main>

    <!-- ── Global overlays ───────────────────────────────────────────── -->
    <alert-snackbar v-model="alert" />
    <user-change-password
      v-model="showChangePassword"
      v-on:save="changePassword"
    />
    <wire-guard-template v-model="showWireGuardTemplate" />
  </v-app>
</template>

<script>
import AlertSnackbar from "./components/AlertSnackbar";
import UserChangePassword from "./components/UserChangePassword";
import WireGuardTemplate from "./components/WireGuardTemplate";

export default {
  name: "App",

  components: { AlertSnackbar, UserChangePassword, WireGuardTemplate },

  data: () => ({
    appTitle: "",
    appVersion: "",
    alert: null,
    showChangePassword: false,
    showWireGuardTemplate: null
  }),

  created() {
    this.appTitle   = process.env.VUE_APP_TITLE;
    this.appVersion = process.env.VUE_APP_VERSION;

    this.$alert.addListener(alert => (this.alert = alert));

    const saved = localStorage.getItem('evpn-theme');
    if (saved) this.$vuetify.theme.dark = saved === 'dark';

    this.$server.defaultErrorHandler = (error, errorFilter) => {
      if (error.status === 401) {
        this.session_loggedIn = false;
        this.$router.replace({ path: "/login" });
      } else if (
        errorFilter === null ||
        typeof errorFilter !== "function" ||
        !errorFilter(error)
      ) {
        this.$alert.raiseHttpError(error);
      }
    };
  },

  mounted() {
    this.$server
      .query({ url: "users/logged" })
      .then(data => (this.session_loggedUser = data));
  },

  methods: {
    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      localStorage.setItem('evpn-theme', this.$vuetify.theme.dark ? 'dark' : 'light');
    },

    logout() {
      this.$server.execute({ url: "users/logout" });
      this.session_loggedIn = false;
      this.$router.replace({ path: "/login" });
    },

    changePassword(data) {
      this.$server
        .update({
          url: "/users/change-password",
          data: { oldPassword: data.oldPassword, newPassword: data.newPassword }
        })
        .then(() => (this.showChangePassword = false));
    }
  }
};
</script>

<style>
/* ── Topbar ─────────────────────────────────────────────────────────── */
.topbar { border-bottom: 1px solid rgba(128,128,128,.15) !important; }
.topbar__title { font-size: 15px; font-weight: 600; }

/* ── Sidebar internals ───────────────────────────────────────────────── */
.sidebar-logo {
  display: flex; align-items: center; gap: 10px;
  padding: 18px 16px 14px;
  border-bottom: 1px solid rgba(255,255,255,.06);
  margin-bottom: 4px;
}
.sidebar-logo__img { width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0; object-fit: contain; }
.sidebar-logo__name    { color: #fff; font-weight: 700; font-size: 14px; line-height: 1.3; }
.sidebar-logo__version { color: rgba(255,255,255,.3); font-size: 10px; }

.sidebar-section {
  font-size: 10px !important; font-weight: 700 !important;
  letter-spacing: .08em !important; color: rgba(255,255,255,.25) !important;
  height: 28px !important; padding-left: 8px !important;
}

.nav-item { border-radius: 8px !important; margin: 1px 0; }
.nav-item .v-list-item__icon { margin-right: 8px !important; margin-top: 10px !important; margin-bottom: 10px !important; }
.nav-item .v-list-item__title { font-size: 13.5px !important; }

.sidebar-footer {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 14px;
}
.sidebar-footer__info  { flex: 1; min-width: 0; }
.sidebar-footer__name  { font-size: 13px; font-weight: 600; color: #fff;
                          overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sidebar-footer__role  { font-size: 11px; color: rgba(255,255,255,.4); }

/* ── Global utils ────────────────────────────────────────────────────── */
.mono { font-family: 'JetBrains Mono', 'Fira Code', 'Consolas', monospace !important; }

@keyframes pulse-dot {
  0%,100% { box-shadow: 0 0 0 0 rgba(72,187,120,.6); }
  50%      { box-shadow: 0 0 0 4px rgba(72,187,120,0); }
}
.online-dot {
  display: inline-block; width: 7px; height: 7px; border-radius: 50%;
  background: #48BB78; flex-shrink: 0;
  animation: pulse-dot 1.8s infinite;
}
</style>
